import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getStoredPageSize } from '@/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  Globe,
  MoreHorizontal,
  GitBranch,
  Play,
  Share2,
  CheckCircle2,
  Clock,
  ListTodo,
  AlertTriangle,
} from 'lucide-react';
import { GLOBAL_TOOLS } from '@/config/tools';
import { Table, TableCard } from '@/components/application/table/table-fixed';
import { PageTransition } from '@/components/PageTransition';
import ProcessShareModal from './ProcessShareModal';
import { CreateWorkflowModal } from '../tasks/CreateWorkflowModal';

// Generate skeleton placeholder items for loading state
const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
  id: `skeleton-${i}`,
  name: '',
  description: '',
  status: '',
  is_global: false,
  tasks: [],
  isLoading: true,
}));

export default function ProcessesPage() {
  const navigate = useNavigate();
  const { userProfile, company, can, loading: authLoading, permissionsLoaded } = useAuth();
  usePageTitle('Processes');
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth is ready when not loading AND permissions have been loaded
  const authReady = !authLoading && permissionsLoaded;
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAvailability, setFilterAvailability] = useState('all');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [createProcessOpen, setCreateProcessOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(10));

  // Global tools for company-wide navigation (from central config)
  const globalTools = GLOBAL_TOOLS;

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);

  // Redirect non-admins (only after auth has loaded)
  useEffect(() => {
    if (authReady && !can('*')) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [authReady, can, navigate]);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch processes
  const fetchProcesses = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_company_processes', {
        p_company_id: company.id
      });

      if (error) throw error;
      setProcesses(data || []);
    } catch (error) {
      console.error('Error fetching processes:', error);
      toast.error('Failed to load processes');
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  // Fetch on mount (only after auth is ready and user has permission)
  useEffect(() => {
    if (authReady && company?.id && can('*')) {
      fetchProcesses();
    }
  }, [authReady, company?.id, can, fetchProcesses]);

  // Filter processes
  const filteredProcesses = useMemo(() => {
    return processes.filter(process => {
      // Search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matchesName = process.name?.toLowerCase().includes(searchLower);
        const matchesDescription = process.description?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesDescription) return false;
      }

      // Status filter
      if (filterStatus !== 'all' && process.status !== filterStatus) {
        return false;
      }

      // Availability filter
      if (filterAvailability === 'global' && !process.is_global) return false;
      if (filterAvailability === 'shared' && process.is_global) return false;

      return true;
    });
  }, [processes, debouncedSearch, filterStatus, filterAvailability]);

  // Pagination
  const totalPages = Math.ceil(filteredProcesses.length / itemsPerPage);
  const paginatedProcesses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProcesses.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProcesses, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus, filterAvailability]);

  // Handle delete
  const handleDelete = async () => {
    if (!deleteTarget || !company?.id) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_company_process', {
        p_workflow_id: deleteTarget.id,
        p_company_id: company.id
      });

      if (error) throw error;

      setProcesses(prev => prev.filter(p => p.id !== deleteTarget.id));
      toast.success('Process deleted successfully');
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error('Failed to delete process');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  // Handle share modal
  const handleOpenShareModal = (process) => {
    setSelectedProcess(process);
    setShowShareModal(true);
  };

  const handleShareUpdate = () => {
    fetchProcesses();
    setShowShareModal(false);
    setSelectedProcess(null);
  };

  // Direct navigation to create a new blank process (bypasses dialog temporarily)
  const handleCreateNewProcess = () => {
    navigate('/processes/editor');
  };

  // Dialog option handler - kept for future use when dialog is re-enabled
  const handleProcessOptionSelect = (optionId) => {
    setCreateProcessOpen(false);

    if (optionId === 'blank') {
      handleCreateNewProcess();
    } else {
      // TODO: Handle other process creation options (AI, import, templates)
      toast.info(`${optionId} process creation coming soon!`);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getTaskCount = (tasks) => {
    if (!tasks || !Array.isArray(tasks)) return 0;
    return tasks.length;
  };

  // Stats
  const stats = useMemo(() => ({
    total: processes.length,
    published: processes.filter(p => p.status === 'published').length,
    draft: processes.filter(p => p.status === 'draft').length,
    global: processes.filter(p => p.is_global).length,
  }), [processes]);

  // Show loading while auth is being verified
  if (!authReady) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  // Don't render anything if user doesn't have permission (redirect will happen via useEffect)
  if (!can('*')) {
    return null;
  }

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName="Company"
          toolName="Processes"
          currentTool="processes"
          spaceTools={globalTools}
          isGlobalView={true}
          actions={
            <div className='flex items-center gap-3'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                <Input
                  type='text'
                  placeholder='Search processes...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='pl-10 bg-white w-64'
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 bg-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterAvailability} onValueChange={setFilterAvailability}>
                <SelectTrigger className="w-36 bg-white">
                  <SelectValue placeholder="Availability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="shared">Shared</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size='sm'
                onClick={handleCreateNewProcess}
              >
                <Plus className='h-4 w-4 mr-2' />
                Create Process
              </Button>
            </div>
          }
        />

        {/* Main Content Container */}
        <div className='max-w-7xl mx-auto mt-4'>
          {/* Stats Cards */}
          <div className='grid grid-cols-4 gap-4 mb-6'>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Total Processes</p>
                  <p className='text-2xl font-bold'>{stats.total}</p>
                </div>
                <GitBranch className='h-8 w-8 text-gray-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Published</p>
                  <p className='text-2xl font-bold text-green-600'>{stats.published}</p>
                </div>
                <CheckCircle2 className='h-8 w-8 text-green-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Drafts</p>
                  <p className='text-2xl font-bold text-amber-600'>{stats.draft}</p>
                </div>
                <Clock className='h-8 w-8 text-amber-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Global</p>
                  <p className='text-2xl font-bold text-blue-600'>{stats.global}</p>
                </div>
                <Globe className='h-8 w-8 text-blue-400' />
              </div>
            </div>
          </div>

          {/* Processes Table */}
          <TableCard.Root>
            <TableCard.Header
              title="Company processes"
              badge={`${filteredProcesses.length} processes`}
            />
            <Table aria-label="Company processes">
              <Table.Header>
                <Table.Head id="name" label="Process" isRowHeader className="w-[30%]" />
                <Table.Head id="status" label="Status" className="w-[12%]" />
                <Table.Head id="availability" label="Availability" className="w-[16%]" />
                <Table.Head id="tasks" label="Tasks" className="w-[10%]" />
                <Table.Head id="runs" label="Runs" className="w-[10%]" />
                <Table.Head id="creator" label="Created By" className="w-[14%]" />
                <Table.Head id="actions" label="" className="w-[8%]" />
              </Table.Header>

              <Table.Body items={loading ? skeletonItems : paginatedProcesses}>
                {(process) => (
                  <Table.Row id={process.id}>
                    {/* Process Name & Description */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-3 w-56" />
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {process.name}
                          </div>
                          {process.description && (
                            <div className="text-sm text-gray-500 truncate">
                              {process.description}
                            </div>
                          )}
                        </div>
                      )}
                    </Table.Cell>

                    {/* Status */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <Skeleton className="h-5 w-16" />
                      ) : (
                        <Badge
                          variant="outline"
                          className={
                            process.status === 'published'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }
                        >
                          {process.status === 'published' ? 'Published' : 'Draft'}
                        </Badge>
                      )}
                    </Table.Cell>

                    {/* Availability */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <Skeleton className="h-5 w-20" />
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {process.is_global ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Globe className="h-3 w-3 mr-1" />
                              All Spaces
                            </Badge>
                          ) : process.shared_space_count > 0 ? (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              <Users className="h-3 w-3 mr-1" />
                              {process.shared_space_count} {process.shared_space_count === 1 ? 'space' : 'spaces'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                              Not shared
                            </Badge>
                          )}
                        </div>
                      )}
                    </Table.Cell>

                    {/* Tasks Count */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <Skeleton className="h-4 w-8" />
                      ) : (
                        <div className="flex items-center gap-1 text-gray-600">
                          <ListTodo className="h-4 w-4" />
                          <span>{getTaskCount(process.tasks)}</span>
                        </div>
                      )}
                    </Table.Cell>

                    {/* Runs Count */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <Skeleton className="h-4 w-8" />
                      ) : (
                        <div className="flex items-center gap-1 text-gray-600">
                          <Play className="h-4 w-4" />
                          <span>{process.run_count || 0}</span>
                        </div>
                      )}
                    </Table.Cell>

                    {/* Creator */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-6 w-6 rounded-full" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={process.creator_avatar_url} />
                            <AvatarFallback className="text-xs bg-gray-100">
                              {getInitials(process.creator_first_name, process.creator_last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-gray-600 truncate">
                            {process.creator_first_name || 'Unknown'}
                          </span>
                        </div>
                      )}
                    </Table.Cell>

                    {/* Actions */}
                    <Table.Cell>
                      {process.isLoading ? (
                        <Skeleton className="h-8 w-8" />
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/processes/${process.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/processes/editor/${process.id}`)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenShareModal(process)}>
                              <Share2 className="h-4 w-4 mr-2" />
                              Manage Sharing
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setDeleteTarget(process);
                                setShowDeleteDialog(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {/* Pagination */}
            {!loading && filteredProcesses.length > itemsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProcesses.length)} of {filteredProcesses.length}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredProcesses.length === 0 && (
              <div className="text-center py-12">
                <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No processes found</h3>
                <p className="text-gray-500 mb-4">
                  {debouncedSearch || filterStatus !== 'all' || filterAvailability !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Create your first company process to get started'}
                </p>
                {!debouncedSearch && filterStatus === 'all' && filterAvailability === 'all' && (
                  <Button onClick={handleCreateNewProcess}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Process
                  </Button>
                )}
              </div>
            )}
          </TableCard.Root>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="sm:max-w-md p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <AlertDialogTitle className="text-lg font-semibold">Delete Process</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone.
                  </AlertDialogDescription>
                </div>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete <span className="font-medium text-foreground">"{deleteTarget?.name}"</span>?
                  This will permanently remove the process and all associated workflow runs.
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Process'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Share Modal */}
        {showShareModal && selectedProcess && (
          <ProcessShareModal
            process={selectedProcess}
            open={showShareModal}
            onOpenChange={setShowShareModal}
            onUpdate={handleShareUpdate}
          />
        )}

        {/* Create Process Modal */}
        <CreateWorkflowModal
          open={createProcessOpen}
          onOpenChange={setCreateProcessOpen}
          onSelectOption={handleProcessOptionSelect}
          isCompanyProcess={true}
        />
      </PageTransition>
    </AppShell3>
  );
}
